import { TbRobotOff } from "solid-icons/tb";
import type { Component } from "solid-js";
import { LinearLogo } from "~/components/integrations/IntegrationSection";
import {
  GmailIcon,
  GoogleCalendarIcon,
  GoogleDriveIcon,
  NotionIcon,
  SlackIcon,
} from "~/components/knowledge/pages/ScopePickerEmptyState";
import type { TranslationKey } from "~/i18n/translations";

/**
 * The marketing pages' editable data: who we show, what they say, the numbers,
 * and the logo wall. Customer names, quotes, metrics and logos are deliberately
 * not translated; every other string on the pages goes through the i18n catalog.
 */

// TODO(amalucelli): customer logos, metrics and testimonials are still mock data, so every
// place that shows them is hidden behind this switch until the real data lands. The
// components stay wired up (rather than commented out) so their styles keep compiling and CI
// doesn't flag them as dead code. Flip to true once CUSTOMER_LOGOS, METRICS and TESTIMONIALS
// are real. Hidden today: the homepage logo/metrics strip and testimonials, the testimonials
// on /for-executives, and the "Trusted by" row and quote on /pricing.
export const SHOW_SOCIAL_PROOF: boolean = false;

// Pending approval for chief.bot: these are the Storytell-era logos the signup card still shows.
export const CUSTOMER_LOGOS: { name: string; src: string; color: string }[] = [
  { name: "Accenture", src: "/images/home/accenture.svg", color: "#A100FF" },
  { name: "Amazon", src: "/images/home/amazon.svg", color: "#FF9900" },
  { name: "Cloudflare", src: "/images/home/cloudflare.svg", color: "#F38020" },
  { name: "Google", src: "/images/home/google.svg", color: "#4285F4" },
  { name: "IBM", src: "/images/home/ibm.svg", color: "#0F62FE" },
  { name: "Meta", src: "/images/home/meta.svg", color: "#0081FB" },
  { name: "Paramount", src: "/images/home/paramount.svg", color: "#0064FF" },
  { name: "PwC", src: "/images/home/pwc.svg", color: "#D04A02" },
  { name: "Samsung", src: "/images/home/samsung.svg", color: "#1428A0" },
  { name: "SAP", src: "/images/home/sap.svg", color: "#0FAAFF" },
  { name: "Shopify", src: "/images/home/shopify.svg", color: "#7AB55C" },
  { name: "T-Mobile", src: "/images/home/t-mobile.svg", color: "#E20074" },
  { name: "Tata", src: "/images/home/tata.svg", color: "#486AAE" },
];

/**
 * A headline number. `value` counts up from zero when the card scrolls into view
 * and `unit` follows it ("3h"). `visual` is the card's small picture: `line` and
 * `bars` plot `points` against their own maximum. `placeholder` tags an invented
 * figure on the page; drop it once the number is measured.
 */
export type Metric = {
  value: number;
  unit?: string;
  label: string;
  color: string;
  placeholder?: boolean;
  visual: { kind: "line" | "bars"; points: number[] } | { kind: "icon"; icon: Component<{ class?: string }> };
};

// The first two are invented until real, measured numbers exist. Not claims: replace or remove before launch.
export const METRICS: Metric[] = [
  {
    value: 3,
    unit: "h",
    label: "saved per week per team lead",
    color: "#c49a1a",
    placeholder: true,
    visual: { kind: "line", points: [1, 1.2, 1.1, 1.8, 2.1, 2.6, 3] },
  },
  {
    value: 12,
    label: "follow-ups filed per meeting",
    color: "#3049cf",
    placeholder: true,
    visual: { kind: "bars", points: [5, 7, 6, 9, 10, 12] },
  },
  { value: 0, label: "bots in the call", color: "#1d9e75", visual: { kind: "icon", icon: TbRobotOff } },
];

/**
 * Placeholders until real, permissioned quotes arrive: every person, company and
 * figure below is invented. The `featured` entry is the large quote; the rest
 * fill the wall beside it. `color` tints the company mark and the person's
 * monogram. `photo` and `logo` take image paths and replace the monogram and the
 * company initial when set. `context` is the team or meeting the quote comes
 * from, and `highlight` the one result worth pulling out of it.
 */
export type Testimonial = {
  quote: string;
  name: string;
  role: string;
  company: string;
  color: string;
  context?: string;
  highlight?: { value: string; label: string };
  featured?: boolean;
  photo?: string;
  logo?: string;
};

export const TESTIMONIALS: Testimonial[] = [
  {
    quote: "Placeholder: 3 hours a week I no longer spend chasing what we agreed.",
    name: "Sam Sample",
    role: "Chief of Staff",
    company: "Globex",
    color: "#059669",
    context: "Leadership syncs",
    highlight: { value: "3h", label: "back every week" },
    featured: true,
  },
  {
    quote: "Placeholder: we file 12 follow-ups a week before the call even ends.",
    name: "Jane Doe",
    role: "VP Operations",
    company: "Acme",
    color: "#e11d48",
    context: "Weekly ops review",
    highlight: { value: "12", label: "follow-ups a week" },
  },
  {
    quote: "Placeholder: planning call at 10, Claude Code had the branch up by 11.",
    name: "John Smith",
    role: "Staff Engineer",
    company: "Hooli",
    color: "#0284c7",
    context: "Sprint planning",
  },
  {
    quote: "Placeholder: reading the decisions back saves us a week of follow-up email.",
    name: "Alex Example",
    role: "Head of Product",
    company: "Initech",
    color: "#7c3aed",
    context: "Product reviews",
  },
  {
    quote: "Placeholder: half our standups now end with the tickets already in Linear.",
    name: "Pat Placeholder",
    role: "Engineering Manager",
    company: "Umbrella",
    color: "#d97706",
    context: "Daily standups",
  },
  {
    quote: "Placeholder: our whole leadership team watches the board call live from the browser.",
    name: "Chris Mock",
    role: "COO",
    company: "Soylent",
    color: "#65a30d",
    context: "Board meetings",
  },
  {
    quote: "Placeholder: the customer recap is in their inbox before I've closed the tab.",
    name: "Robin Test",
    role: "Account Executive",
    company: "Massive Dynamic",
    color: "#4f46e5",
    context: "Customer calls",
  },
];

// Stays empty until the App Store listing is public; the iPhone link renders only once it's set.
export const IOS_APP_URL: string = "";

// The ids are public: a deep link names the video to open by its id, so a renamed id keeps its old
// name as an alias. `poster` is a real frame
// from the video, `duration` is shown as written ("1:30"), and `width`/`height` are the video's
// pixel size, which shapes the player box before the file loads.
export const VIDEOS = {
  "exec-demo": {
    src: "/videos/how-chief-works-executive.mp4",
    poster: "/videos/how-chief-works-executive-poster.jpg",
    duration: "3:14",
    width: 2560,
    height: 1440,
    title: "marketing.video.demo_title",
  },
} satisfies Record<
  string,
  { src: string; poster: string; duration: string; width: number; height: number; title: TranslationKey }
>;

export type VideoId = keyof typeof VIDEOS;

/**
 * How a logo is drawn.
 * - `mask`: a single-colour Simple Icons glyph filled with `color`. A `dark`
 *   brand (near-black) switches to the text colour in the dark theme so it
 *   doesn't vanish; a `light` brand does the same in the light theme.
 * - `svg`: a full-colour mark drawn inline; `color` only tints its tile.
 * - `image`: a vendor's own colour mark. A `tile` mark is its own square ground;
 *   a `plate` mark is dark ink that needs a light ground under it in both themes.
 *   `color` only tints its tile; mono brands without a colour of their own get slate.
 */
export type LogoMark =
  | { kind: "mask"; src: string; color: string; tone?: "dark" | "light" }
  | { kind: "svg"; icon: Component<{ class?: string }>; color: string }
  | { kind: "image"; src: string; color: string; ground?: "tile" | "plate" };

export type Logo = { name: string; mark: LogoMark };

const mask = (slug: string, color: string, tone?: "dark" | "light"): LogoMark => ({
  kind: "mask",
  src: `/images/logos/${slug}.svg`,
  color,
  tone,
});

const MONO = "#64748B";

const agent = (file: string, color: string, ground?: "tile" | "plate"): LogoMark => ({
  kind: "image",
  src: `/images/agents/${file}`,
  color,
  ground,
});

export const MEETING_APPS: Logo[] = [
  { name: "Zoom", mark: mask("zoom", "#0B5CFF") },
  { name: "Google Meet", mark: mask("googlemeet", "#00897B") },
  { name: "Microsoft Teams", mark: mask("microsoftteams", "#6264A7") },
  { name: "Slack", mark: { kind: "svg", icon: SlackIcon, color: "#4A154B" } },
  { name: "Webex", mark: mask("webex", "#00A0D1") },
];

/**
 * The integrations Chief ships itself: a line someone says in a meeting, what
 * Chief then does in the tool, and the things it can do there. Each claim maps
 * to a tool Chief has, and every write waits for the user's approval.
 */
export const BUILT_IN: (Logo & { quote: TranslationKey; result: TranslationKey; verbs: TranslationKey })[] = [
  {
    name: "Gmail",
    mark: { kind: "svg", icon: GmailIcon, color: "#EA4335" },
    quote: "marketing.home.integrations.quote_gmail",
    result: "marketing.home.integrations.result_gmail",
    verbs: "marketing.home.integrations.verbs_gmail",
  },
  {
    name: "Google Calendar",
    mark: { kind: "svg", icon: GoogleCalendarIcon, color: "#4285F4" },
    quote: "marketing.home.integrations.quote_calendar",
    result: "marketing.home.integrations.result_calendar",
    verbs: "marketing.home.integrations.verbs_calendar",
  },
  {
    name: "Google Drive",
    mark: { kind: "svg", icon: GoogleDriveIcon, color: "#1E88E5" },
    quote: "marketing.home.integrations.quote_drive",
    result: "marketing.home.integrations.result_drive",
    verbs: "marketing.home.integrations.verbs_drive",
  },
  {
    name: "Slack",
    mark: { kind: "svg", icon: SlackIcon, color: "#4A154B" },
    quote: "marketing.home.integrations.quote_slack",
    result: "marketing.home.integrations.result_slack",
    verbs: "marketing.home.integrations.verbs_slack",
  },
  {
    name: "Notion",
    mark: { kind: "svg", icon: NotionIcon, color: "#000000" },
    quote: "marketing.home.integrations.quote_notion",
    result: "marketing.home.integrations.result_notion",
    verbs: "marketing.home.integrations.verbs_notion",
  },
  {
    name: "Linear",
    mark: { kind: "svg", icon: LinearLogo, color: "#5E6AD2" },
    quote: "marketing.home.integrations.quote_linear",
    result: "marketing.home.integrations.result_linear",
    verbs: "marketing.home.integrations.verbs_linear",
  },
  {
    name: "GitHub",
    mark: mask("github", "#181717", "dark"),
    quote: "marketing.home.integrations.quote_github",
    result: "marketing.home.integrations.result_github",
    verbs: "marketing.home.integrations.verbs_github",
  },
];

export const AGENTS: Logo[] = [
  { name: "Claude Code", mark: mask("claude", "#D97757") },
  { name: "Codex", mark: mask("openai", "#000000", "dark") },
  { name: "Cursor", mark: agent("cursor.svg", MONO, "tile") },
  { name: "GitHub Copilot", mark: agent("copilot.svg", "#8534F3", "plate") },
  { name: "OpenCode", mark: agent("opencode.svg", MONO, "plate") },
  { name: "Cline", mark: agent("cline.svg", MONO, "plate") },
  { name: "Augment", mark: agent("augment.svg", MONO, "plate") },
  { name: "Factory Droid", mark: agent("droid.svg", "#EF6F2E", "tile") },
  { name: "Goose", mark: agent("goose.svg", MONO, "plate") },
  { name: "Junie", mark: agent("junie.svg", "#47E054") },
  { name: "Kiro CLI", mark: agent("kiro.svg", "#9046FF", "tile") },
  { name: "Kimi CLI", mark: agent("kimi.png", MONO, "tile") },
  { name: "Qwen Code", mark: agent("qwen.svg", "#615CED") },
  { name: "Mistral Vibe", mark: agent("mistral-vibe.svg", "#FA520F", "plate") },
  { name: "OpenHands", mark: agent("openhands.svg", MONO, "plate") },
  { name: "Poolside", mark: agent("poolside.svg", "#5B5BF7") },
  { name: "BLACKBOX AI", mark: agent("blackbox.png", MONO, "tile") },
  { name: "Stakpak", mark: agent("stakpak.png", "#16A3B5", "tile") },
];

const MCP: [name: string, slug: string, color: string, tone?: "dark" | "light"][] = [
  ["HubSpot", "hubspot", "#FF7A59"],
  ["Salesforce", "salesforce", "#00A1E0"],
  ["Jira", "jira", "#0052CC"],
  ["Figma", "figma", "#F24E1E"],
  ["Stripe", "stripe", "#635BFF"],
  ["Atlassian", "atlassian", "#0052CC"],
  ["Asana", "asana", "#F06A6A"],
  ["Airtable", "airtable", "#18BFFF"],
  ["Box", "box", "#0061D5"],
  ["Brex", "brex", "#212121", "dark"],
  ["Calendly", "calendly", "#006BFF"],
  ["Canva", "canva", "#00C4CC"],
  ["Clerk", "clerk", "#6C47FF"],
  ["ClickUp", "clickup", "#7B68EE"],
  ["Cloudflare", "cloudflare", "#F38020"],
  ["Contentful", "contentful", "#2478CC"],
  ["Dropbox", "dropbox", "#0061FF"],
  ["Gusto", "gusto", "#F45D48"],
  ["Hugging Face", "huggingface", "#FFD21E", "light"],
  ["Intercom", "intercom", "#6AFDEF", "light"],
  ["Make", "make", "#6D00CC"],
  ["Miro", "miro", "#050038", "dark"],
  ["Mixpanel", "mixpanel", "#7856FF"],
  ["Neon", "neon", "#34D59A"],
  ["Netlify", "netlify", "#00C7B7"],
  ["New Relic", "newrelic", "#1CE783"],
  ["PagerDuty", "pagerduty", "#06AC38"],
  ["PayPal", "paypal", "#002991"],
  ["PlanetScale", "planetscale", "#000000", "dark"],
  ["PostHog", "posthog", "#000000", "dark"],
  ["Postman", "postman", "#FF6C37"],
  ["Prisma", "prisma", "#2D3748", "dark"],
  ["QuickBooks", "quickbooks", "#2CA01C"],
  ["Render", "render", "#000000", "dark"],
  ["Sanity", "sanity", "#0D0E12", "dark"],
  ["Sentry", "sentry", "#362D59", "dark"],
  ["Shopify", "shopify", "#7AB55C"],
  ["Supabase", "supabase", "#3FCF8E"],
  ["SurveyMonkey", "surveymonkey", "#00BF6F"],
  ["Todoist", "todoist", "#E44332"],
  ["Vercel", "vercel", "#000000", "dark"],
  ["Webflow", "webflow", "#146EF5"],
  ["Wix", "wix", "#0C6EFC"],
  ["WordPress", "wordpress", "#21759B"],
  ["Zapier", "zapier", "#FF4F00"],
  ["Zoom", "zoom", "#0B5CFF"],
  ["Cal.com", "caldotcom", "#292929", "dark"],
  ["Lucid", "lucid", "#282C33", "dark"],
  ["Mintlify", "mintlify", "#18E299"],
];

const mcp = (file: string, color: string, ground?: "tile" | "plate"): LogoMark => ({
  kind: "image",
  src: `/images/mcp/${file}`,
  color,
  ground,
});

// Remote MCP servers from the catalog: reachable through MCP, not integrations Chief ships.
export const MCP_SERVERS: Logo[] = [
  ...MCP.map(([name, slug, color, tone]) => ({ name, mark: mask(slug, color, tone) })),
  { name: "Ahrefs", mark: mcp("ahrefs.png", "#0A5D99", "tile") },
  { name: "Amplitude", mark: mcp("amplitude.png", "#0052F2", "tile") },
  { name: "Apify", mark: mcp("apify.svg", "#F86606") },
  { name: "Apollo.io", mark: mcp("apollo.svg", "#EBF212", "tile") },
  { name: "Attio", mark: mcp("attio.png", MONO, "plate") },
  { name: "Axiom", mark: mcp("axiom.png", MONO, "tile") },
  { name: "Browserbase", mark: mcp("browserbase.svg", "#FF4500", "tile") },
  { name: "Clay", mark: mcp("clay.png", "#50D0FF") },
  { name: "Close", mark: mcp("close.png", "#4EC375") },
  { name: "Context7", mark: mcp("context7.png", "#007A55", "tile") },
  { name: "Exa", mark: mcp("exa.png", "#1F40ED", "tile") },
  { name: "Firecrawl", mark: mcp("firecrawl.png", "#FA5D19", "plate") },
  { name: "Fireflies", mark: mcp("fireflies.png", "#CF3487") },
  { name: "Front", mark: mcp("front.png", "#A050F0", "plate") },
  { name: "Gamma", mark: mcp("gamma.svg", "#002253") },
  { name: "Granola", mark: mcp("granola.svg", "#B2C248", "tile") },
  { name: "Honeycomb", mark: mcp("honeycomb.png", "#0298EC", "plate") },
  { name: "incident.io", mark: mcp("incident.png", "#F25533", "tile") },
  { name: "Klaviyo", mark: mcp("klaviyo.png", MONO, "tile") },
  { name: "Lovable", mark: mcp("lovable.svg", "#FE7702") },
  { name: "Mercury", mark: mcp("mercury.svg", MONO, "plate") },
  { name: "monday.com", mark: mcp("monday.png", "#FFCC00", "plate") },
  { name: "Ramp", mark: mcp("ramp.png", "#E4F222") },
  { name: "Stytch", mark: mcp("stytch.png", MONO, "tile") },
  { name: "Superhuman", mark: mcp("superhuman.svg", MONO, "plate") },
  { name: "Tally", mark: mcp("tally.svg", MONO, "plate") },
  { name: "Tavily", mark: mcp("tavily.svg", MONO, "tile") },
  { name: "WorkOS", mark: mcp("workos.png", "#6363F1", "tile") },
  { name: "Zight", mark: mcp("zight.svg", "#007AFF") },
];

export const MODEL_PROVIDERS: Logo[] = [
  { name: "Anthropic", mark: mask("anthropic", "#191919", "dark") },
  { name: "OpenAI", mark: mask("openai", "#000000", "dark") },
  { name: "OpenRouter", mark: mask("openrouter", "#94A3B8") },
];
